import { Injectable } from "@angular/core";

@Injectable({
  providedIn: 'root'
})
export class TicketUtilsService {
  constructor() {}

  /**
   * Returns Bootstrap color class based on progress percentage and status
   * @param progress - Completion percentage (0-100)
   * @param status - Ticket status string
   * @returns Bootstrap background color class
   */
  getProgressColor(progress: number, status: string): string {
    if (status === 'Completed' || this.isTicketComplete(status)) {
      return 'bg-success';
    }
    if (progress < 25) return 'bg-danger';
    if (progress < 50) return 'bg-warning';
    if (progress < 75) return 'bg-primary';
    return 'bg-success';
  }

  /**
   * Checks if ticket status indicates completion
   * @param status - Ticket status string
   * @returns true if status indicates completion
   */
  private isTicketComplete(status: string): boolean {
    if (!status) return false;

    const statusName = status.toLowerCase().trim();
    const completionKeywords = [
      'complete', 'completed', 'done', 'finished',
      'closed', 'resolved', 'solved'
    ];

    return completionKeywords.some((keyword) => statusName.includes(keyword));
  }

  /**
   * Returns status color classes for ticket status badges
   * @param status - Ticket status string
   * @returns Bootstrap color classes
   */
  getStatusColor(status: string): string {
    switch(status) {
      case 'Open': return 'text-primary bg-primary-subtle';
      case 'In Progress': return 'text-warning bg-warning-subtle';
      case 'Pending': return 'text-info bg-info-subtle';
      case 'Completed': return 'text-success bg-success-subtle';
      case 'Closed': return 'text-success bg-success-subtle';
      case 'Blocked': return 'text-danger bg-danger-subtle';
      default: return 'text-secondary bg-secondary-subtle';
    }
  }

  /**
   * Returns priority border styling for ticket cards
   * @param priority - Priority level string
   * @returns Bootstrap border classes
   */
  getPriorityBorder(priority: string): string {
    switch(priority?.toLowerCase()) {
      case 'high': return 'border-start border-4 border-warning';
      case 'urgent': return 'border-start border-4 border-danger';
      case 'critical': return 'border-start border-4 border-danger';
      case 'low': return 'border-start border-4 border-success';
      default: return '';
    }
  }

  /**
   * Returns priority label with color styling
   * @param priority - Priority level string
   * @returns Object with label and color classes
   */
  getPriorityLabel(priority: string): { label: string, color: string } {
    switch(priority?.toLowerCase()) {
      case 'urgent': return { label: 'Urgent', color: 'bg-danger-subtle text-danger' };
      case 'critical': return { label: 'Critical', color: 'bg-danger-subtle text-danger' };
      case 'high': return { label: 'High', color: 'bg-warning-subtle text-warning' };
      case 'normal': return { label: 'Normal', color: 'bg-primary-subtle text-primary' };
      case 'low': return { label: 'Low', color: 'bg-success-subtle text-success' };
      default: return { label: 'Normal', color: 'bg-primary-subtle text-primary' };
    }
  }
}
